/**
 * RetentionTab
 *
 * Settings for the single log cleaner (CCMS.Worker.ScheduledJobs > LogRetentionJob): per log
 * table, switch cleaning on or off, choose how many days to keep, tune the batch size and time
 * budget, ask for a run now, and see what the last run did.
 * Backend: CCMS.Api.System api/log-retention (Admin only).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconAlertCircle,
  IconAdjustments,
  IconArrowBackUp,
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconInfoCircle,
  IconPlayerPlay,
  IconRefresh,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Alert,
  Text,
  Badge,
  Code,
  Switch,
  NumberInput,
  SimpleGrid,
  Collapse,
  Skeleton,
  Tooltip,
  EmptyState,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/advanced';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useLogRetentionService,
  LogRetentionLimits,
  LogRetentionRunStatusIds,
} from '@actschurch/ccms-sdk';
import type { LogRetentionPolicy, LogRetentionPolicyUpdate } from '@actschurch/ccms-sdk';

const QUERY_KEY = ['admin', 'system-log', 'log-retention'] as const;

const STATUS_COLOR: Record<number, string> = {
  [LogRetentionRunStatusIds.Succeeded]: 'green',
  [LogRetentionRunStatusIds.Partial]: 'yellow',
  [LogRetentionRunStatusIds.Failed]: 'red',
  [LogRetentionRunStatusIds.Skipped]: 'gray',
  [LogRetentionRunStatusIds.Running]: 'blue',
};

const errorMessage = (err: unknown, fallback: string): string => {
  const message = (err as { message?: string } | null)?.message;
  return message && message.trim().length > 0 ? message : fallback;
};

const formatNumber = (value?: number | null): string =>
  value === null || value === undefined ? 'Not known yet' : value.toLocaleString('en-ZA');

const formatSize = (mb?: number | null): string => {
  if (mb === null || mb === undefined) return 'Not known yet';
  if (mb >= 1024) return `${(mb / 1024).toLocaleString('en-ZA', { maximumFractionDigits: 1 })} GB`;
  return `${mb.toLocaleString('en-ZA', { maximumFractionDigits: 1 })} MB`;
};

const formatWhen = (iso?: string | null): string => {
  if (!iso) return 'Never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Never';
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  const absolute = date.toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  if (minutes < 1) return `Just now (${absolute})`;
  if (minutes < 60) return `${minutes} min ago (${absolute})`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago (${absolute})`;
  return absolute;
};

/** "Application logs" -> "application logs", but "API request logs" stays as is. */
const lowerFirst = (s: string): string =>
  s.length > 1 && /[A-Z]/.test(s[1]) ? s : s.charAt(0).toLowerCase() + s.slice(1);

const toForm = (p: LogRetentionPolicy): LogRetentionPolicyUpdate => ({
  isEnabled: p.isEnabled,
  retentionDays: p.retentionDays,
  batchSize: p.batchSize,
  maxRunMinutes: p.maxRunMinutes,
});

const validate = (f: LogRetentionPolicyUpdate): string | null => {
  const L = LogRetentionLimits;
  if (!Number.isInteger(f.retentionDays) || f.retentionDays < L.minRetentionDays || f.retentionDays > L.maxRetentionDays)
    return `Keep logs for between ${L.minRetentionDays} and ${L.maxRetentionDays.toLocaleString('en-ZA')} days.`;
  if (!Number.isInteger(f.batchSize) || f.batchSize < L.minBatchSize || f.batchSize > L.maxBatchSize)
    return `Batch size must be between ${L.minBatchSize} and ${L.maxBatchSize.toLocaleString('en-ZA')} rows.`;
  if (!Number.isInteger(f.maxRunMinutes) || f.maxRunMinutes < L.minRunMinutes || f.maxRunMinutes > L.maxRunMinutes)
    return `Maximum run time must be between ${L.minRunMinutes} and ${L.maxRunMinutes} minutes.`;
  return null;
};

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const content = (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600}>
        {value}
      </Text>
    </Stack>
  );
  return hint ? (
    <Tooltip label={hint} multiline w={260} withArrow>
      <div>{content}</div>
    </Tooltip>
  ) : (
    content
  );
}

interface PolicyCardProps {
  policy: LogRetentionPolicy;
  onSaved: (updated: LogRetentionPolicy) => void;
  onRunRequested: () => void;
}

function PolicyCard({ policy, onSaved, onRunRequested }: PolicyCardProps) {
  const service = useLogRetentionService();
  const [form, setForm] = useState<LogRetentionPolicyUpdate>(() => toForm(policy));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Pick up server changes (after a save or a refresh) unless the admin is mid-edit.
  const serverForm = useMemo(() => toForm(policy), [policy]);
  const dirty =
    form.isEnabled !== serverForm.isEnabled ||
    form.retentionDays !== serverForm.retentionDays ||
    form.batchSize !== serverForm.batchSize ||
    form.maxRunMinutes !== serverForm.maxRunMinutes;
  const [lastServerForm, setLastServerForm] = useState(serverForm);
  useEffect(() => {
    const changed =
      lastServerForm.isEnabled !== serverForm.isEnabled ||
      lastServerForm.retentionDays !== serverForm.retentionDays ||
      lastServerForm.batchSize !== serverForm.batchSize ||
      lastServerForm.maxRunMinutes !== serverForm.maxRunMinutes;
    if (changed) {
      setLastServerForm(serverForm);
      setForm(serverForm);
    }
  }, [serverForm, lastServerForm]);

  const validationError = validate(form);
  const statusId = policy.lastRunStatusID ?? null;
  const isRunning = statusId === LogRetentionRunStatusIds.Running;
  const isFailed = statusId === LogRetentionRunStatusIds.Failed;

  const save = async () => {
    setInlineError(null);
    if (validationError) {
      setInlineError(validationError);
      return;
    }
    setSaving(true);
    try {
      const updated = await service.updateLogRetentionPolicy(policy.policyID, form);
      onSaved(updated);
      toast.success('Retention saved', `${policy.displayName}: keep ${form.retentionDays} days${form.isEnabled ? '' : ' (cleaning switched off)'}.`);
    } catch (err) {
      const message = errorMessage(err, 'Could not save the retention settings. Please try again.');
      setInlineError(message);
      toast.error('Not saved', message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setInlineError(null);
    setRunning(true);
    try {
      await service.runLogRetentionNow(policy.policyID);
      toast.success('Run requested', `The cleaner starts on ${lowerFirst(policy.displayName)} within a minute.`);
      onRunRequested();
    } catch (err) {
      const message = errorMessage(err, 'Could not request a run. Please try again.');
      setInlineError(message);
      toast.error('Run not requested', message);
    } finally {
      setRunning(false);
      setConfirmRun(false);
    }
  };

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 240 }}>
            <Group gap="xs" wrap="wrap">
              <Text fw={700} size="lg">
                {policy.displayName}
              </Text>
              <Code>{policy.tableName}</Code>
              {!form.isEnabled && (
                <Badge color="gray" variant="outline">
                  Switched off
                </Badge>
              )}
            </Group>
            {policy.description && (
              <Text size="sm" c="dimmed">
                {policy.description}
              </Text>
            )}
          </Stack>
          <Group gap="xs">
            {policy.runNowRequested && (
              <Badge color="blue" variant="light">
                Run requested
              </Badge>
            )}
            {policy.lastRunStatusName ? (
              <Badge color={STATUS_COLOR[statusId ?? 0] ?? 'gray'} variant="light">
                {policy.lastRunStatusName}
              </Badge>
            ) : (
              <Badge color="gray" variant="light">
                Not run yet
              </Badge>
            )}
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Switch
            label="Clean this table automatically"
            description="When off, nothing is deleted from this table."
            checked={form.isEnabled}
            onChange={(e) => setForm((f) => ({ ...f, isEnabled: e.currentTarget.checked }))}
          />
          <NumberInput
            label="Keep logs for (days)"
            description="Rows older than this are deleted."
            min={LogRetentionLimits.minRetentionDays}
            max={LogRetentionLimits.maxRetentionDays}
            allowDecimal={false}
            allowNegative={false}
            value={form.retentionDays}
            onChange={(v) => setForm((f) => ({ ...f, retentionDays: typeof v === 'number' ? v : Number(v) || 0 }))}
          />
        </SimpleGrid>

        <div>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconAdjustments size={14} />}
            rightSection={showAdvanced ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            onClick={() => setShowAdvanced((s) => !s)}
          >
            Advanced settings
          </Button>
          <Collapse in={showAdvanced}>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mt="sm">
              <NumberInput
                label="Batch size (rows per step)"
                description={`Starting size of each delete step. The cleaner adjusts it between a quarter and four times this value to keep each step to a second or two, never above ${LogRetentionLimits.maxBatchSize.toLocaleString('en-ZA')}. Use a smaller value for tables with very large rows.`}
                min={LogRetentionLimits.minBatchSize}
                max={LogRetentionLimits.maxBatchSize}
                step={50}
                allowDecimal={false}
                allowNegative={false}
                value={form.batchSize}
                onChange={(v) => setForm((f) => ({ ...f, batchSize: typeof v === 'number' ? v : Number(v) || 0 }))}
              />
              <NumberInput
                label="Maximum run time (minutes)"
                description="How long one run may spend on this table. Anything left over is picked up in the next hourly run."
                min={LogRetentionLimits.minRunMinutes}
                max={LogRetentionLimits.maxRunMinutes}
                allowDecimal={false}
                allowNegative={false}
                value={form.maxRunMinutes}
                onChange={(v) => setForm((f) => ({ ...f, maxRunMinutes: typeof v === 'number' ? v : Number(v) || 0 }))}
              />
            </SimpleGrid>
          </Collapse>
        </div>

        <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
          <Stat label="Last run" value={isRunning ? `Running since ${formatWhen(policy.lastRunAt)}` : formatWhen(policy.lastRunAt)} />
          <Stat label="Rows deleted (last run)" value={formatNumber(policy.lastRunDeleted)} />
          <Stat
            label="Rows still to clean"
            value={formatNumber(policy.rowsRemaining)}
            hint="An estimate of rows that are already older than the retention period and will be deleted in the next runs."
          />
          <Stat label="Rows in table" value={formatNumber(policy.tableRowCount)} hint="Approximate, from the database's own statistics." />
          <Stat label="Table size" value={formatSize(policy.tableSizeMb)} hint="Data plus indexes. This is space used inside the database." />
        </SimpleGrid>

        {policy.lastError && (
          <Alert
            color={isFailed ? 'red' : 'yellow'}
            icon={<IconAlertCircle size={16} />}
            title={isFailed ? 'Last error' : 'Last run note'}
          >
            <Text size="sm" style={{ wordBreak: 'break-word' }}>
              {policy.lastError}
            </Text>
          </Alert>
        )}

        {(inlineError || (dirty && validationError)) && (
          <Text size="sm" c="red">
            {inlineError ?? validationError}
          </Text>
        )}

        <Group justify="space-between" wrap="wrap">
          <Text size="xs" c="dimmed">
            {policy.modifiedBy ? `Settings last changed by ${policy.modifiedBy}` : ''}
          </Text>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconArrowBackUp size={16} />}
              disabled={!dirty || saving}
              onClick={() => {
                setForm(serverForm);
                setInlineError(null);
              }}
            >
              Undo changes
            </Button>
            <Tooltip
              label={form.isEnabled ? 'Start cleaning this table now instead of waiting for the hourly run' : 'Switch cleaning on (and save) before running it'}
              withArrow
            >
              <div>
                <Button
                  variant="light"
                  leftSection={<IconPlayerPlay size={16} />}
                  disabled={!policy.isEnabled || dirty || policy.runNowRequested || isRunning}
                  loading={running}
                  onClick={() => setConfirmRun(true)}
                >
                  Run now
                </Button>
              </div>
            </Tooltip>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              disabled={!dirty || !!validationError}
              loading={saving}
              onClick={save}
            >
              Save
            </Button>
          </Group>
        </Group>
      </Stack>

      <ConfirmDialog
        opened={confirmRun}
        onClose={() => setConfirmRun(false)}
        onConfirm={runNow}
        loading={running}
        type="warning"
        title={`Clean ${lowerFirst(policy.displayName)} now?`}
        message={`Rows older than ${policy.retentionDays} days are deleted permanently, in small steps for up to ${policy.maxRunMinutes} minutes. The cleaner picks this up within a minute and keeps going until the table is clean or the request lapses after an hour.`}
        confirmLabel="Run now"
        cancelLabel="Cancel"
        closeOnConfirm={false}
      />
    </Card>
  );
}

export function RetentionTab() {
  const service = useLogRetentionService();
  const queryClient = useQueryClient();

  const policies = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => service.getLogRetentionPolicies(),
    // Poll while a run is going or waiting, so the numbers update without a manual refresh.
    refetchInterval: (query) => {
      const data = query.state.data as LogRetentionPolicy[] | undefined;
      const busy = data?.some((p) => p.runNowRequested || p.lastRunStatusID === LogRetentionRunStatusIds.Running);
      return busy ? 15_000 : false;
    },
  });

  const replaceOne = (updated: LogRetentionPolicy) => {
    queryClient.setQueryData<LogRetentionPolicy[]>(QUERY_KEY, (old) =>
      (old ?? []).map((p) => (p.policyID === updated.policyID ? updated : p))
    );
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return (
    <Stack gap="md">
      <Alert color="blue" icon={<IconInfoCircle size={16} />} title="How log cleaning works">
        <Stack gap={6}>
          <Text size="sm">
            One cleaner runs every hour and deletes log rows that are older than the number of days you choose for each
            table. It works in small steps and pauses between them, so CCMS keeps running normally while it cleans.
          </Text>
          <Text size="sm">
            Deleting rows frees space inside the database, which is then reused for new logs, so the database stops
            growing. The server disk itself does not get smaller, and its size on the hosting dashboard stays the same.
          </Text>
        </Stack>
      </Alert>

      <Group justify="flex-end">
        <Button
          variant="subtle"
          leftSection={<IconRefresh size={16} />}
          onClick={refresh}
          loading={policies.isFetching}
        >
          Refresh
        </Button>
      </Group>

      {policies.isLoading ? (
        <Stack gap="md">
          <Skeleton height={220} radius="md" />
          <Skeleton height={220} radius="md" />
          <Skeleton height={220} radius="md" />
        </Stack>
      ) : policies.isError ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the retention settings">
          <Stack gap="xs">
            <Text size="sm">{errorMessage(policies.error, 'The System service did not respond.')}</Text>
            <Group>
              <Button size="xs" variant="light" color="red" onClick={refresh}>
                Try again
              </Button>
            </Group>
          </Stack>
        </Alert>
      ) : (policies.data ?? []).length === 0 ? (
        <EmptyState
          title="No retention settings yet"
          description="The settings table has not been set up. The cleaner still runs with its built-in defaults: application logs 30 days, API request logs 14 days and database query logs 7 days."
        />
      ) : (
        (policies.data ?? []).map((policy) => (
          <PolicyCard key={policy.policyID} policy={policy} onSaved={replaceOne} onRunRequested={refresh} />
        ))
      )}
    </Stack>
  );
}

export default RetentionTab;
